import { Facebook, Twitter, Linkedin, Instagram, ChevronLeft, ChevronRight } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Newsletter from '@/components/sections/Newsletter'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useJob, useJobs, useIncrementJobView } from '@/lib/api/hooks/job'
import type { Job } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { sanitizeHtml } from '@/utils/helpers/html'
import ApplicationForm from '@/components/pages/Opportunities/Careers/ApplicationForm'

// Helper function to format job type from API to display format
const formatJobType = (jobType: string): string => {
  const jobTypeMap: Record<string, string> = {
    'FULL_TIME': 'Fulltime',
    'PER_TIME': 'Part-time',
    'CONTRACT': 'Contract',
    'TEMPORARY': 'Temporary'
  }
  return jobTypeMap[jobType] || jobType
}

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

// Helper function to format date for display (e.g., "12 Mar 25")
const formatDateShort = (dateString: string): string => {
  const date = new Date(dateString)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`
}

// Helper function to get job status based on dates
const getJobStatus = (startDate: string, endDate: string): 'Open' | 'Closed' => {
  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (now >= start && now <= end) {
    return 'Open'
  } else {
    return 'Closed'
  }
}

const JobDetail = () => {
  const navigate = useNavigate()
  const { jobId } = useParams<{ jobId: string }>()
  const hasIncrementedView = useRef(false)

  // Fetch current job
  const { data: jobData, isLoading, error } = useJob(jobId)

  // Fetch other jobs for sidebar
  const { data: otherJobsData } = useJobs({ page: 1, size: 10 }, {})

  // Increment view mutation
  const incrementViewMutation = useIncrementJobView()

  const apiJob = jobData?.data
  const allJobs = otherJobsData?.data?.data || []

  const isJobOpen = apiJob ? getJobStatus(apiJob.startDate, apiJob.endDate) === 'Open' : false
  // Increment view count when job is loaded
  useEffect(() => {
    if (jobId && apiJob && !hasIncrementedView.current) {
      hasIncrementedView.current = true
      incrementViewMutation.mutate(jobId)
    }
  }, [jobId, apiJob])

  // Find current job index for navigation
  const currentJobIndex = allJobs.findIndex((j: Job) => j.jobId === jobId)
  const hasPrevious = currentJobIndex > 0
  const hasNext = currentJobIndex >= 0 && currentJobIndex < allJobs.length - 1
  const previousJob = hasPrevious ? allJobs[currentJobIndex - 1] : null
  const nextJob = hasNext ? allJobs[currentJobIndex + 1] : null

  // Get similar jobs for sidebar (excluding current job)
  const similarJobs = allJobs
    .filter((j: Job) => j.jobId !== jobId)
    .slice(0, 3)

  const handleShare = (platform: string) => {
    if (!apiJob) return

    const url = encodeURIComponent(window.location.href)
    const title = encodeURIComponent(`${apiJob.jobTitle} - African Space Agency`)

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank')
        break
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank')
        break
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
        break
      case 'instagram':
        navigator.clipboard.writeText(window.location.href)
        alert('Link copied to clipboard! You can share this on Instagram.')
        break
      default:
        navigator.clipboard.writeText(window.location.href)
    }
  }

  const handleApplyForJob = () => {
    // Scroll to application form
    const formElement = document.getElementById('application-form')
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading job details..." />
  }

  if (error || !apiJob) {
    return (
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">Job not found or error loading job.</p>
          <button
            onClick={() => navigate('/opportunities/careers/jobs')}
            className="px-4 py-2 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    )
  }

  const jobStatus = getJobStatus(apiJob.startDate, apiJob.endDate)

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Jobs', href: '/opportunities/careers/jobs' },
    { label: apiJob.jobTitle, href: '#', active: true }
  ]

  return (
    <motion.div
      className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="mb-5" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Main Content Grid */}
      <motion.div
        className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportReveal}
      >
        {/* Left Content */}
        <div className="lg:col-span-2 order-1">
          <div className="space-y-8">
            {/* Navigation Buttons */}
            {(hasPrevious || hasNext) && (
              <div className="flex items-center justify-between gap-4 pb-4 border-b border-gray-200">
                <button
                  onClick={() => previousJob && navigate(`/opportunities/careers/${previousJob.jobId}`)}
                  disabled={!hasPrevious}
                  className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasPrevious
                    ? 'text-gray-900 hover:bg-gray-200'
                    : 'text-gray-400 cursor-not-allowed'
                    }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm font-medium font-satoshi">Previous</span>
                </button>

                <button
                  onClick={() => nextJob && navigate(`/opportunities/careers/${nextJob.jobId}`)}
                  disabled={!hasNext}
                  className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${hasNext
                    ? 'text-gray-900 hover:bg-gray-200'
                    : 'text-gray-400 cursor-not-allowed'
                    }`}
                >
                  <span className="text-sm font-medium font-satoshi">Next</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Job Header */}
            <div className="space-y-4">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900">
                {apiJob.jobTitle}
              </h1>

              <div className="flex flex-wrap items-center gap-2 text-base">
                <span className="text-wine-100 font-medium">Location:</span>
                <span className="text-gray-900">{apiJob.jobLocation}</span>
                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>

                <span className="text-wine-100 font-medium">Status:</span>
                <span className={`font-medium ${jobStatus === 'Open' ? 'text-forestGreen-100' : 'text-wine-100'}`}>
                  {jobStatus}
                </span>
                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>

                <span className="text-wine-100 font-medium">Job Type:</span>
                <span className="text-gray-900">{formatJobType(apiJob.jobType)}</span>
                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>

                {apiJob.datePublished && (
                  <>
                    <span className="text-wine-100 font-medium">Posting Date:</span>
                    <span className="text-gray-900">{formatDateShort(apiJob.datePublished)}</span>
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            {apiJob.description && (
              <div className="space-y-4">
                <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900">
                  Description
                </h2>
                <div
                  className=""
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(apiJob.description) }}
                />
              </div>
            )}

            {/* Application Deadline */}
            <div className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900">
                Application Deadline
              </h2>
              <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                {formatDate(apiJob.endDate)}
              </p>
            </div>

            {/* Action Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 pt-4">
              {/* RSS Icon */}
              <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-sm"></div>
              </div>

              {/* Apply Button */}
              {
                isJobOpen ?      <button
                onClick={handleApplyForJob}
                className="px-8 py-3 bg-wine-100 text-white rounded hover:bg-wine-200 transition-colors"
              >
                <span className="text-base font-bold font-satoshi">
                  Apply for Job
                </span>
              </button> : (
                <div className="flex items-center justify-center px-8 py-3 bg-gray-300 text-gray-700 rounded">
                  <span className="text-base font-bold font-satoshi">
                    Application Closed
                  </span>
                </div>
              )
              }
         
            </div>

            {/* Social Sharing */}
            <div className="space-y-4 pt-4">
              <span className="text-lg font-normal font-pt-serif text-gray-600">
                SHARE
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleShare('facebook')}
                  className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                  aria-label="Share on Facebook"
                >
                  <Facebook className="w-full h-full" />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                  aria-label="Share on Twitter"
                >
                  <Twitter className="w-full h-full" />
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="w-6 h-6 text-gray-800 hover:text-forestGreen-100 transition-colors"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="w-full h-full" />
                </button>
                <button
                  onClick={() => handleShare('instagram')}
                  className="w-6 h-6 text-gray-800 hover:text-pink-600 transition-colors"
                  aria-label="Share on Instagram"
                >
                  <Instagram className="w-full h-full" />
                </button>
              </div>
            </div>

{
  isJobOpen ? ( <div id="application-form"><ApplicationForm jobId={apiJob.jobId} /></div> ) : null
}
            
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="lg:col-span-1 order-2">
          <div className="space-y-10">
            {/* Similar Jobs */}
            <div className="space-y-6">
              <h3 className="text-xl sm:text-2xl font-normal font-pt-serif text-gray-900 leading-7">
                SIMILAR JOBS
              </h3>

              <div className="space-y-4">
                {similarJobs.length > 0 ? (
                  similarJobs.map((job: Job) => {
                    const status = getJobStatus(job.startDate, job.endDate)
                    return (
                      <div
                        key={job.jobId}
                        onClick={() => navigate(`/opportunities/careers/${job.jobId}`)}
                        className="block bg-gray-50 rounded-md p-4 hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 space-y-2">
                            <div className={`px-3 py-1 rounded-md inline-flex items-center gap-1.5 ${status === 'Open' ? 'bg-emerald-50' : 'bg-wine-100/10'
                              }`}>
                              <div className={`w-2 h-2 rounded-full ${status === 'Open' ? 'bg-forestGreen-100' : 'bg-wine-100'
                                }`}></div>
                              <span className={`text-sm font-medium font-satoshi ${status === 'Open' ? 'text-forestGreen-100' : 'text-wine-100'
                                }`}>
                                {status}
                              </span>
                            </div>

                            <h4 className="text-base font-medium font-satoshi text-gray-900">
                              {job.jobTitle}
                            </h4>

                            <div className="text-sm text-gray-700 space-y-1">
                              <div>
                                <span className="text-wine-100 font-medium">Location:</span> {job.jobLocation}
                                <span className="mx-2">•</span>
                                <span className="text-wine-100 font-medium">Type:</span> {formatJobType(job.jobType)}
                              </div>
                              {job.datePublished && (
                                <div>
                                  <span className="text-wine-100 font-medium">Posting date:</span> {formatDateShort(job.datePublished)}
                                  <span className="mx-2">•</span>
                                  <span className="text-wine-100 font-medium">End date:</span> {formatDateShort(job.endDate)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <p className="text-gray-600 text-sm">No similar jobs available.</p>
                )}
              </div>
            </div>


            {/* Newsletter */}
            <Newsletter />
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default JobDetail